import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import moment from "moment";
import _ from "lodash";
//common components
import {AppState} from "../config/redux/reducers";
import AppConstants from "../constants";
import UACConstants from "../constants/UACConstants";
import GridWrapper from "../common/GridWrapper";
import Loader from "../common/Loader";
import FullScreenLoader from "../common/FullScreenLoader";
import CustomSnackbar from "../common/CustomSnackbar";
//redux
import {userLogout} from "../Login/redux/loginSlice";
import {
    fetchOrdersFilterChipsDisplayCount,
    fetchPlanningOrderListByFilter,
    resetExportInitiated,
    fetchCarrierDropdown,
    switchCourier,
    confirmCourierSwitch,
    denyCourierSwitch,
    resetCourierState
} from './redux/expressSlice';
//Material UI Components
import {CellParams, SortModel} from "../common/GridWrapper/DataTable";
import {Box, Grid, IconButton, SvgIcon, Switch, Typography } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
//styles
import {useStyles} from "./express.styles";
//assets
import {ReactComponent as TruckIcon} from './../assets/icons/Truck.svg';
import locationIcon from './../assets/icons/map-pin.svg';
//other components
import {TIMER_CONST} from "../constants/CommonConstants"
import {Countdown} from '../utils/countdownTimer'
import { isDelayed, isNonEmptyArray, isRapydActivity } from "../utils/helpers.utils";
import ExpressOrderDetails from "./ExpressOrderDetails";
import DriverAssignment from './DriversDetail/DriverAssignment';
import {
    createChipsCountFilterPayload,
    createFilterPayload,
    expressOrdersDataTransform,
    getOrderStatusField
} from "../mocks/express/response.transforms";
import { getConsignmentStatusField } from "../mocks/SharedTransform/response.transforms";
import {ReactComponent as RapydIcon} from './../assets/icons/Lightning.svg';
import ClubbingSuggetionIcon from "../assets/icons/list-icon-green.svg";
import ClubedIconGrey from "../assets/icons/clubbed_icon_grey.svg";
import ClockIcon from "../assets/icons/clock-v2.svg";
import useExportInitiated from "CustomHook/ExortInitiatedHook";
import CustomTooltip from "common/Tooltip";
import { TextConstants } from "constants/TextConstants";
import ExpressConstants, {FilterChips, EXPRESS_DELIVERY_TYPE} from "constants/ExpressConstants";
import ExportInitiatedDialogProvider from 'ExportIntiaiedDialog/ExportInitiatedDialogProvider';
import { exportToExcel } from 'ExportIntiaiedDialog/redux/exportInitiatedDialogSlice';
import DeliveryConstants from "constants/SharedConstants/DeliveryConstants";
import { resetInvoiceState } from "SharedComponent/redux/sharedSlice";
import CourierDropdown from './CourierSwitching/CourierDropdown';
import CourierSwitchResultDialog from './CourierSwitching/CourierSwitchResultDialog';
import { computeShouldShowCourierSwitchingFilter } from './utils/courierSwitchUtils';
import { isStoreEnabledInConfig } from "utils/productUtils";
import { COLORS } from "config/theme/baseTheme";
import { useUACHelper } from "../contexts/UACContext";
import { useAssignmentAgingColor } from "./useAssignmentAgingColor";
import { AssignmentAgingConfig } from "../config/redux/common/commonSlice.types";

interface ExpressProps {
    match?: any;
    location?: any;
}

interface FiltersDelayed {
    delayedOrders?: {
      value: boolean;
    };
    pageNumber?: number;
    pageSize?: number;
  }

interface DriverNameCellProps {
    name?: string;
    assignmentDate?: string;
    storeCode?: string;
    vehicle?: string;
    agingConfig?: AssignmentAgingConfig;
}

// Driver name in the Assigned bucket ages from default -> orange -> red per
// the store/vehicle delay thresholds in agingConfig (from the CLUBBING config's
// "intelligent-clubbing-driver-indicator" entry), via useAssignmentAgingColor.
// Memoized because renderCell instantiates this per row on every grid render.
export const DriverNameCell = React.memo(({ name, assignmentDate, storeCode, vehicle, agingConfig }: DriverNameCellProps) => {
    const classes = useStyles();
    const agingColor = useAssignmentAgingColor(agingConfig, storeCode, vehicle, assignmentDate);
    return (
        <Grid className={classes.supportDriverHeader} container alignItems="center">
            <Typography
                data-testid="driver-name"
                className="columnValue"
                style={agingColor ? { color: agingColor } : undefined}
            >
                {name || ""}
            </Typography>
        </Grid>
    );
});

const Express = (props: ExpressProps) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const {
        ordersData,
        ordersFilterChipsDisplayCount,
        ordersFiltersDropdowns,
        tableLoading,
        loading,
        clubbingLoading,
        error,
        errorCode,
        excelLoading,
        exportError,
        driverAssigned,
        replanningConsignmentSuccess,
        isAddressUpdated,
        isPolled,
        areOrderCommentsAdded,
        exportInitiated, 
        reportId,
        supportDriverAssigned,
        primaryDriverUnAssigned,
        carrierList,
        switchInfo,
        courierSwitchSuccess,
        courierSwitchCanceled
    } = useSelector(
        (state: AppState) => state.expressDashboard
    );
    const {countryCode, hubCode, pickedUpConfigDetails, clubbingConfigDetails, onDemandConfigDetails, onDemandConfigDetailsStore, assignmentAgingConfigDetails} = useSelector(
        (state: AppState) => state.common
    );
    const uacHelper = useUACHelper();
    const canEditExpressHome = uacHelper.canEdit(UACConstants.ED_HOME);
    const [scrollToTopOnReset, setScrollToTopOnReset] = useState(false);
    const hubCountryCode = sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode;

    const [showTime, setShowTime] = useState(false);
    const [delayedToggle, setDelayedToggle] = useState(false);

    // Replace multiple useState with single state object
    const [courierSwitchState, setCourierSwitchState] = useState({
        isSelectionMode: false,
        selectedOrders: [] as any[],
        courierList: [] as any[],
        selectedCourier: "",
        loadingCouriers: false,
        deSelectCheckbox: false,
        showResultDialog: false,
        switchResults: { successIds: [] as string[], failedIds: [] as string[] },
        isConfirmingSwitch: false
    });

    const onLoadRef = useRef(true);
    // Lets a caller (e.g. the fulfillment dashboard's stage drill-down) land
    // here with the "ALL" chip pre-selected via ?chip=ALL, instead of always
    // opening on DEFAULT_FILTER_CHIP. Any other/missing value falls back to
    // the normal default — this only special-cases the one value callers use.
    const [activeOrdersFilterChip, setActiveOrdersFilterChip] = useState(() => {
        const chipParam = new URLSearchParams(props.location?.search ?? window.location.search).get("chip");
        return chipParam === AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ALL_FILTER_CHIP
            ? chipParam
            : AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DEFAULT_FILTER_CHIP;
    });
    // hubCode legitimately transitions through more than one non-empty value
    // during startup (an initial/stale sessionStorage value, then the real
    // list once hubCodeUpdate's fetch resolves) — so "was hubCode previously
    // non-empty and different" can't tell a real user-driven hub switch apart
    // from startup settling. Instead, lock the chip against any *automatic*
    // reset for as long as it came from a ?chip=ALL drill-down link and the
    // user hasn't yet manually picked a chip themselves (see handleChipFilter,
    // which clears this on the first real click).
    const chipLockedByUrlRef = useRef(
        new URLSearchParams(props.location?.search ?? window.location.search).get("chip") === AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ALL_FILTER_CHIP,
    );

    // Consignment IDs handed off by a stage drill-down (OpsDashboard's
    // onStageDrillDown) — passed via router state rather than the URL since
    // the list can be long. Locked onto every fetch as an extra
    // consignmentCode filter until the user takes over with their own chip
    // pick or search, same lifecycle as chipLockedByUrlRef above.
    const drillDownConsignmentIdsRef = useRef<string[] | undefined>(
        props.location?.state?.consignmentIds?.length ? props.location.state.consignmentIds : undefined,
    );

    const [isOrderSearched, setIsOrderSearched] = useState(false);
    const [ordersSearchValue, setOrdersSearchValue] = useState("");
    const [isTabChange, setIsTabChange] = useState(false);

    const { openExportInitiatedInfo, handleExportInitiatedDialogClose } = useExportInitiated(resetExportInitiated, exportInitiated);
      
    // Check if clubbing is enabled for a given store
    const isClubbingEnabledForStore = useCallback((storeCode: string) => {
      return isStoreEnabledInConfig(storeCode, clubbingConfigDetails);
    }, [clubbingConfigDetails]);

    // Keep a ref so the renderCell in ordersTableWrapperData (initialized once via useState)
    // always calls the latest version of isClubbingEnabledForStore instead of the stale closure.
    const isClubbingEnabledForStoreRef = useRef(isClubbingEnabledForStore);
    isClubbingEnabledForStoreRef.current = isClubbingEnabledForStore;

    const getRapydActivityImage = () => {
        return (
          <SvgIcon
           className="icon"
           component={RapydIcon}
         />
        );
     
    };

    const getClubbedIconImage = () => { 
        return (
          <CustomTooltip title="clubbed order">
           <img src={ClubedIconGrey} alt="clubbed-order" />
          </CustomTooltip>
         );
     };

     const getRtdBreachedIcon = () => { 
        return (
          <CustomTooltip title="Dispatch Quickly">
           <img src={ClockIcon} alt="Dispatch-Quickly" />
          </CustomTooltip>
         );
     }; 

     const getSupportDriver = (drivers: any[]) => {
        return drivers?.length && drivers?.map((driverName: string, index: number) => (
          <Typography key={`tooltipArea-${driverName}-${index}`} className="tooltipItem">{driverName}</Typography>
        ));
      }

    const getSwitchingProgressIcon = () => {
        return (
          <CustomTooltip title={TextConstants.CARRIER_SWITCHING_PROGRESS}>
            <TruckIcon style={{ color: COLORS.BG9E9E9E, fontSize: '18px', marginLeft: '4px' }} />
          </CustomTooltip>
        );
    };

    const getClubbingSuggestionIcon = () => {
        return (
          <CustomTooltip title="Clubbing Suggestion">
           <img src={ClubbingSuggetionIcon} alt="clubbed-order" style={{ width: '18px', height: '18px' }} />
          </CustomTooltip>
        );
     };

    const renderConsignmentIdCell = useCallback((params: CellParams) => (
      <Grid container direction="row" alignItems="center">
        <Grid item>
          <Typography
            className={
              isDelayed(params?.rowData?.orderEtaStatus)
                ? `idLink delayedLink ${courierSwitchState.isSelectionMode ? 'disabled' : ''}`
                : `idLink ${courierSwitchState.isSelectionMode ? 'disabled' : ''}`
            }
            style={
              params?.rowData?.inSwitchingProgress
                ? { color: COLORS.BG9E9E9E, cursor: 'not-allowed' }
                : courierSwitchState.isSelectionMode
                ? { color: COLORS.BG9E9E9E }
                : {}
            }
          >
            {params.value}
          </Typography>
        </Grid>
        {isRapydActivity(params?.rowData?.activity) && (
          <> {getRapydActivityImage()}</>
        )}
        {params?.rowData?.assignedConsignments?.length > 1 && (
          <> {getClubbedIconImage()}</>
        )}
        {params?.rowData?.inSwitchingProgress && (
          <> {getSwitchingProgressIcon()}</>
        )}
        {isClubbingEnabledForStoreRef.current(params?.rowData?.hubCode) && (
          params?.rowData?.hasClubbingSuggestion
            ? <>{getClubbingSuggestionIcon()}</>
            : params?.rowData?.rtdBreached && <>{getRtdBreachedIcon()}</>
        )}
      </Grid>
    ), [courierSwitchState.isSelectionMode]);

    // Same ref pattern as isClubbingEnabledForStoreRef — keeps useState's renderCell pointing to
    // the latest closure without needing to rebuild it in the useEffect below.
    const renderConsignmentIdCellRef = useRef(renderConsignmentIdCell);
    renderConsignmentIdCellRef.current = renderConsignmentIdCell;

    const renderDriverNameCell = useCallback((params: CellParams) => (
        <DriverNameCell
          name={params.value}
          assignmentDate={
            params?.rowData?.deliveryStatus === EXPRESS_DELIVERY_TYPE.ASSIGNED
              ? params?.rowData?.assignmentDate
              : undefined
          }
          storeCode={params?.rowData?.hubCode}
          vehicle={params?.rowData?.vehicle}
          agingConfig={assignmentAgingConfigDetails}
        />
    ), [assignmentAgingConfigDetails]);

    // Same ref pattern as renderConsignmentIdCellRef — assignmentAgingConfigDetails
    // loads asynchronously after mount, so the driverName renderCell (baked into
    // ordersTableWrapperData's one-time useState initializer below) must read the
    // latest config through a ref rather than closing over it directly, or it stays
    // permanently undefined for the lifetime of this component.
    const renderDriverNameCellRef = useRef(renderDriverNameCell);
    renderDriverNameCellRef.current = renderDriverNameCell;

    const [ordersTableWrapperData, setOrdersTableWrapperData] = useState({
        ...AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.TABLE_WRAPPER_DATA,
        HeaderData: AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.TABLE_WRAPPER_DATA.HeaderData.map((header: any) => {
            let newHeader = {...header};
            if (newHeader.field === "consignmentId") {
              newHeader.renderCell = (params: CellParams) => renderConsignmentIdCellRef.current(params);
            }
            if (newHeader.field === 'status') {
                newHeader.renderCell = (params: any) => (
                    params.value ?
                        <Grid className={classes.statusHeaderContainer} container alignItems="center">
                            <Typography
                                className={['orderTag', getOrderStatusField('color', params.value),].join(" ")}>{params.value ? params.value : ""}</Typography>
                        </Grid>
                        : <></>
                );
            }
            if (newHeader.field === 'deliveryStatus') {
                newHeader.renderCell = (params: any) => (
                    params.value ?
                        <Grid className={classes.statusHeaderContainer} container alignItems="center">
                            <Typography
                                className={['tag', getConsignmentStatusField('color', params.value),].join(" ")}>{params.value ? params.value : ""}</Typography>
                        </Grid>
                        : <></>
                );
            }
            if (newHeader.field === 'driverName') {
                newHeader.renderCell = (params: CellParams) => renderDriverNameCellRef.current(params);
            }
            if (newHeader.field === 'supportDriverName') {
                newHeader.renderCell = (params: CellParams) => {
                  return (
                    <Grid container className={classes.supportDriverHeader}>
                      <CustomTooltip title={getSupportDriver(params.value)}>
                        <Typography className="columnValue">{params.value?.length > 1 ? `${TextConstants.MULTIPLE}(${params.value?.length})` : params?.value?.[0]}</Typography>
                      </CustomTooltip>
                    </Grid>
                  );
                }
              }
            if (newHeader.field === 'actions') {
                newHeader.renderCell = (params: CellParams) => (
                    <Typography 
                      className={`actionsLink ${courierSwitchState.isSelectionMode ? 'disabled' : ''}`}
                    >
                      {params.value}
                    </Typography>
                )
            }
            if (newHeader.field === 'deliveryArea') {
                newHeader.renderCell = (params: CellParams) => (
                    <Grid container>
                        <Grid item>{params.value}</Grid>
                        <Grid item className={classes.infoSection}>
                            <img src={locationIcon} alt="info-icon" className={classes.infoIconStyle}/>
                        </Grid>
                    </Grid>
                )
            }
            return newHeader;
        }),
        defaultFiltersObj: {
            ...AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.TABLE_WRAPPER_DATA.defaultFiltersObj, dateRange: {
                ...AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.TABLE_WRAPPER_DATA.defaultFiltersObj.dateRange,
                value: {
                    startDate: moment().format(AppConstants.DATE_FORMAT),
                    endDate: moment().format(AppConstants.DATE_FORMAT)
                }
            }
        }
    });

    const [ordersActiveFilters, setOrdersActiveFilters] = useState({...ordersTableWrapperData.defaultFiltersObj});
    const [ordersListToShow, setOrdersListToShow] = useState([]);
    const [isDisplayCount, setIsDisplayCount] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [openConsignmentDetailsPopup, setOpenConsignmentDetailsPopup] = useState(false);
    const [openDriverAssignmentPopup, setOpenDriverAssignmentPopup] = useState(false);
    const [consignmentData, setConsignmentData] = useState({});
    const [renderExcelProgress, setRenderExcelProgress] = useState(false);
    const snackbarType = useRef(AppConstants.SNACKBAR.TYPES.SUCCESS);
    const snackbarMessage = useRef("");

    const handleDownloadLogs = useCallback(
        (consignment: any) => {
            if (ordersActiveFilters) {
                let payloadFilters: any = _.cloneDeep(ordersActiveFilters);
                payloadFilters.pageSize = ordersTableWrapperData.loadMore.pageSize;
                const payload = createFilterPayload(payloadFilters, delayedToggle, ordersData, false, hubCountryCode, activeOrdersFilterChip);
                payload.searchAttribute = {
                    key: 'consignmentCode',
                    value: consignment?.consignmentCode
                }
                dispatch(
                    exportToExcel({
                        payload: payload,
                        url: DeliveryConstants.REPORT_URL.EXPRESS_LOGS
                    })
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, ordersData, hubCountryCode, activeOrdersFilterChip, ordersActiveFilters, delayedToggle]
    )

    const handleExcelExport = useCallback(
        (filters: any) => {
            if (filters) {
                let payloadFilters = _.cloneDeep(filters);
                payloadFilters.pageSize = ordersTableWrapperData.loadMore.pageSize;
                const payload = createFilterPayload(payloadFilters, delayedToggle, ordersData, false, hubCountryCode, activeOrdersFilterChip);
                dispatch(
                    exportToExcel({
                        payload: payload,
                        url: DeliveryConstants.REPORT_URL.EXPRESS_ORDERS
                    })
                );
            }
            setRenderExcelProgress(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, ordersData, hubCountryCode, activeOrdersFilterChip, delayedToggle]
    )

    const handleOrdersHeaderButtonClick = useCallback(
        (btnObj: any) => {
            if (btnObj && btnObj.type) {
                switch (btnObj.type) {
                    case 'excel':
                        handleExcelExport(ordersActiveFilters);
                        break;
                    default:
                        break;
                }
            }
        },
        [ordersActiveFilters, handleExcelExport]
    );

    const ordersSearchFilterHandler = (data: { [key: string]: string }, updatedSearchValue: string, filters: any) => {
        const activeFilters = _.cloneDeep(filters);
        onLoadRef.current = false;
        setOrdersSearchValue(updatedSearchValue);
        setIsOrderSearched(false);
        // A manual search always wins from here on, same as a manual chip click.
        drillDownConsignmentIdsRef.current = undefined;
        if (updatedSearchValue) {
            setIsOrderSearched(true);
            for (const searchKey in data) {
                if (data[searchKey]) {
                    activeFilters.searchAttribute = {
                        key: searchKey,
                        value: data[searchKey]
                    }
                    ordersFilterCallback(activeFilters);
                    return;
                }
            }
        }
    };

   

    const getFilterChipsCount = useCallback(
        (payload: any) => {
            setIsDisplayCount(false);
            dispatch(
                fetchOrdersFilterChipsDisplayCount({
                    payload: payload
                })
            );
            setIsDisplayCount(true);
        },
        [dispatch],
    )

    const ordersFilterCallback = useCallback(
        (filters: any, isLoadMore?: boolean, isChipFilterClicked?: boolean, selectedChip?: any, delayedClicked?: boolean) => {
            if (filters) {
                let payloadFilters = _.cloneDeep(filters);
                const effectiveDelayedClicked = delayedClicked !== undefined ? delayedClicked : delayedToggle;

                payloadFilters.pageSize = ordersTableWrapperData.loadMore.pageSize;
                if (!isChipFilterClicked) {
                    selectedChip = activeOrdersFilterChip;
                }
                const payload = createFilterPayload(payloadFilters, effectiveDelayedClicked, ordersData, isLoadMore, hubCountryCode, selectedChip);
                let payloadFilters2 = _.cloneDeep(filters);
                payloadFilters2.pageSize = ordersTableWrapperData.loadMore.pageSize;
                const payloadForCount = createChipsCountFilterPayload(payloadFilters2, effectiveDelayedClicked, ordersData, isLoadMore, hubCountryCode);
                if (drillDownConsignmentIdsRef.current?.length) {
                    const consignmentCodeFilter = { key: 'consignmentCodes', value: drillDownConsignmentIdsRef.current };
                    payload.attributes.push(consignmentCodeFilter);
                    payloadForCount.attributes.push(consignmentCodeFilter);
                }
                getFilterChipsCount(payloadForCount);
                if (isLoadMore) {
                    setIsTabChange(false);
                }
                dispatch(
                    fetchPlanningOrderListByFilter({
                        filters: payload
                    })
                );
                setOrdersActiveFilters(filters);
            }
            setRenderExcelProgress(false);
        },
        [ordersData, ordersTableWrapperData, activeOrdersFilterChip, dispatch, hubCountryCode, getFilterChipsCount, delayedToggle],
    );

    const getFilterChips = () => {
        const filterChips = AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.FILTER_CHIPS;
        const showPickedUp = isNonEmptyArray( pickedUpConfigDetails) && pickedUpConfigDetails[0]?.value === true;
        const chipsToRender = filterChips.filter((chip: any) => {
            if (chip.code === AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.PICKED_UP) {
                return showPickedUp;
            }
            return true;
        });
        return chipsToRender.map((chip: any, index: number) => (
            <Grid
              key={`chipFilterItem-${index}`}
              item
              className={`chipFilterItem ${showPickedUp ? "pickUpBucket" : ""} 
              ${index === 0 ? "firstItem" : ""}
              ${activeOrdersFilterChip === chip.code ? "active" : ""}`}
              onClick={() => handleChipFilter(chip, ordersActiveFilters)}
            >
                <Grid container className="chipContainer">
                    <Grid item className="chipItem name">
                        <Typography>{chip.value}</Typography>
                    </Grid>
                    <Grid item className="chipItem count">
                        <Typography>{ordersFilterChipsDisplayCount.consignmentCount[chip.code]}</Typography>
                    </Grid>
                </Grid>
            </Grid>
        ))
    };

    const handleConsignmentCellClick = useCallback(
        (params: CellParams) => {
            // Prevent any action if row is in switching progress
            if (params.rowData?.inSwitchingProgress) {
                openSnackbarPopup(TextConstants.CARRIER_SWITCHING_ORDER_IN_PROGRESS, AppConstants.SNACKBAR.TYPES.ERROR);
                return;
            }

            // Prevent actions when in selection mode (checkboxes are visible)
            if (courierSwitchState.isSelectionMode) {
                return;
            }

            if (params.field === 'consignmentId') {
                const selectedRow: any = params.rowData;
                const selectedConsignment = ordersData.elements.filter((item: any) => item.consignmentCode === selectedRow.consignmentId);
                setConsignmentData(selectedConsignment.length ? selectedConsignment[0] : {});
                setOpenConsignmentDetailsPopup(true);
            }
            if (params.field === 'actions') {
                const selectedRow: any = params.rowData;
                if (selectedRow.actions !== null && selectedRow.actions !== "") {
                    const selectedConsignment = ordersData.elements.filter((item: any) => item.consignmentCode === selectedRow.consignmentId);
                    setConsignmentData(selectedConsignment.length ? selectedConsignment[0] : {});
                    setOpenDriverAssignmentPopup(true);
                }
            }
        },
        [ordersData, courierSwitchState.isSelectionMode]
    );

    const openSnackbarPopup = (msg: string, type: string) => {
        snackbarMessage.current = msg;
        snackbarType.current = type;
        setOpenSnackbar(true);
    }

    const handleSnackbarClose = useCallback(
        () => {
            setOpenSnackbar(false);
        },
        [],
    );

    const handleSnackbarExited = useCallback(
        () => {
            if (errorCode === AppConstants.EXPRESS_CONSTANTS.RESPONSE_CONSTANTS.ERROR_CODES.SESSION_TIMEOUT) {
                dispatch(
                    userLogout()
                );
            }
        },
        [dispatch, errorCode]
    );

    const handleProgressClose = useCallback(
        () => {
            setRenderExcelProgress(false);
        },
        [],
    );

    const handleLoadMoreDisableState = useCallback(
        () => {
            let loadMoreState = ordersTableWrapperData.loadMore;
            loadMoreState.disabled = ordersData.totalPages ? ordersData.pageNumber + 1 >= ordersData.totalPages : true;
            setOrdersTableWrapperData({...ordersTableWrapperData, loadMore: loadMoreState});
        },
        [ordersTableWrapperData, ordersData]
    );

    const shouldShowDelayedFilter = useCallback((activeChip: string): boolean => {
        return AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.POTENTIAL_DELAY_STATUSES.includes(activeChip as FilterChips);
    }, []);

    const shouldShowCourierSwitchingFilter = useCallback((activeChip: string): boolean => {
        const hasCourierSwitchAccess = uacHelper.hasButtonAccess(UACConstants.ED_HOME_COURIER_SWITCH_BTN, 'CLICK');

        if (!hasCourierSwitchAccess) return false;

        return computeShouldShowCourierSwitchingFilter({
            activeChip,
            courierSwitchStatuses: AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.COURIER_SWITCH_FILTER_STATUSES,
            ordersCount: ordersListToShow.length,
            onDemandConfigDetails,
            onDemandConfigDetailsStore,
            hubCode,
        });
    }, [onDemandConfigDetails, onDemandConfigDetailsStore, hubCode, ordersListToShow, uacHelper]);

    const shouldShowCustomFilter = useCallback((activeChip: string): boolean => {
        return shouldShowDelayedFilter(activeChip) || shouldShowCourierSwitchingFilter(activeChip);
    }, [shouldShowDelayedFilter, shouldShowCourierSwitchingFilter]);

    const resetCourierSwitchState = useCallback(() => {
        setCourierSwitchState({
            isSelectionMode: false,
            selectedOrders: [],
            courierList: courierSwitchState.courierList, // Keep courier list
            selectedCourier: "",
            loadingCouriers: false,
            deSelectCheckbox: false,
            showResultDialog: false,
            switchResults: { successIds: [] as string[], failedIds: [] as string[] },
            isConfirmingSwitch: false
        });
    }, [courierSwitchState.courierList]);

    const triggerDeselectCheckbox = useCallback(() => {
        setCourierSwitchState(prev => ({ ...prev, deSelectCheckbox: true }));
        requestAnimationFrame(() => 
            setCourierSwitchState(prev => ({ ...prev, deSelectCheckbox: false }))
        );
    }, []);

    const handleChipFilter = useCallback(
        (chip: any, activeFilters: any) => {
          const filters = { ...activeFilters };
          setIsTabChange(true);
          onLoadRef.current = false;
          // A real, manual chip click always wins from here on — stop
          // protecting a ?chip=ALL drill-down's initial value now that the
          // user has taken over.
          chipLockedByUrlRef.current = false;
          drillDownConsignmentIdsRef.current = undefined;
          setIsOrderSearched(false);
          
          // Reset courier switch state when changing chips
          if (courierSwitchState.isSelectionMode) {
            resetCourierSwitchState();
            triggerDeselectCheckbox();
          }
          
          if (chip.code !== activeOrdersFilterChip) {
            setActiveOrdersFilterChip(chip.code);
            // Reset delayedToggle if the new chip does not match the custom filter condition
            if (!shouldShowCustomFilter(chip.code)) {
              setDelayedToggle(false);
              ordersFilterCallback(filters, false, true, chip.code, false); // Explicitly send false
            } else {
              ordersFilterCallback(filters, false, true, chip.code, delayedToggle); // Send the current state of delayedToggle
            }
          } else {
            ordersFilterCallback(filters, false, true, chip.code, delayedToggle); // Send the current state of delayedToggle
          }
          setOrdersActiveFilters(filters);
          setRenderExcelProgress(false);
        },
        [ordersFilterCallback, activeOrdersFilterChip, delayedToggle, shouldShowCustomFilter, courierSwitchState.isSelectionMode, resetCourierSwitchState, triggerDeselectCheckbox]
      );

    const closeConsignmentDetailsPopup = useCallback(
        () => {
            setOpenConsignmentDetailsPopup(false);
            dispatch(resetInvoiceState());
        },
        [],
    );

    const closeDriverAssignmentPopup = useCallback(
        () => {
            setOpenDriverAssignmentPopup(false);
            setConsignmentData({});
        }, []
    );

    useEffect(() => {
        if (!canEditExpressHome) {
            let headerDataWithoutActions = ordersTableWrapperData.HeaderData.map((data: any) => {
                if (data.field === "actions") {
                    data.hide = true;
                }
                return data;
            });
            setOrdersTableWrapperData({...ordersTableWrapperData, HeaderData: headerDataWithoutActions});
        }
    }, [canEditExpressHome]);

    useEffect(() => {
        if (error) {
            openSnackbarPopup(error, AppConstants.SNACKBAR.TYPES.ERROR);
        }
    }, [error])

    useEffect(() => {
        if (isOrderSearched && isDisplayCount) {
            const chips = Object.keys(ordersFilterChipsDisplayCount.consignmentCount).filter((key: string) => ordersFilterChipsDisplayCount.consignmentCount[key] > 0);
            let selectedChip;
            let selectedChipValue;
            if (chips.length === 2) {
                selectedChip = chips.filter((key: string) => key !== AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ALL_FILTER_CHIP);
                selectedChipValue = selectedChip[0];
            } else {
                selectedChip = chips.filter((key: string) => key === AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ALL_FILTER_CHIP);
                selectedChipValue = selectedChip[0];
            }
            handleChipFilter({code: chips.length ? selectedChipValue : activeOrdersFilterChip}, ordersActiveFilters)
        } else {
            setOrdersListToShow(expressOrdersDataTransform(ordersData) as any);
            handleLoadMoreDisableState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordersFilterChipsDisplayCount, ordersData]);

    useEffect(() => {
        if ((driverAssigned || supportDriverAssigned || primaryDriverUnAssigned) && !clubbingLoading) {
            const snackbarMessage = primaryDriverUnAssigned ? TextConstants.DRIVER_UNASSIGNED_SUCCESS_MSG : AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DRIVER_ASSIGNED_SUCCESS_MSG
            openSnackbarPopup(snackbarMessage, AppConstants.SNACKBAR.TYPES.SUCCESS);
            setIsOrderSearched(false);
            ordersFilterCallback(ordersActiveFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverAssigned, supportDriverAssigned, primaryDriverUnAssigned, clubbingLoading])

    useEffect(() => {
        if (isAddressUpdated) {
            openSnackbarPopup(AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.ADDRESS_MARKED_UNVERIFIED, AppConstants.SNACKBAR.TYPES.SUCCESS);
            setIsOrderSearched(false);
            ordersFilterCallback(ordersActiveFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAddressUpdated])

    useEffect(() => {
        if (replanningConsignmentSuccess || isPolled) {
            openSnackbarPopup(AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.REPLANNING_CONSIGNMENT_SUCCESS_MSG, AppConstants.SNACKBAR.TYPES.SUCCESS);
            setIsOrderSearched(false);
            ordersFilterCallback(ordersActiveFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replanningConsignmentSuccess, isPolled])

    useEffect(() => {
        if (areOrderCommentsAdded) {
            setIsOrderSearched(false);
            ordersFilterCallback(ordersActiveFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areOrderCommentsAdded])

    useEffect(() => {
        const ordersHeaderData = ordersTableWrapperData.HeaderData.map((header: any) => {
            if (header.filterObj) {
                if (header.filterObj.fieldName === 'hub') {
                    header.filterObj.items = ordersFiltersDropdowns.hubCodeList || [];
                }
                if (header.filterObj.fieldName === 'hubName') {
                    header.filterObj.items = ordersFiltersDropdowns.hubNameList || [];
                }
                if (header.filterObj.fieldName === 'deliverySlotTime') {
                    header.filterObj.items = ordersFiltersDropdowns.deliverySlotList || [];
                }
                if (header.filterObj.fieldName === 'deliveryStatus') {
                    header.filterObj.items = ordersFiltersDropdowns.statusList || [];
                }
                if (header.filterObj.fieldName === 'deliveryArea') {
                    header.filterObj.items = ordersFiltersDropdowns.deliveryArea || [];
                }
                if (header.filterObj.fieldName === "addressStatus") {
                    header.filterObj.items = ordersFiltersDropdowns.addressStatusList || [];
                }
                if (header.filterObj.fieldName === "activity") {
                    header.filterObj.items = ordersFiltersDropdowns.activityList || [];
                }
                if (header.filterObj.fieldName === "deliveryZone") {
                    header.filterObj.items = ordersFiltersDropdowns.deliveryZonesList || [];
                }
                if (header.filterObj.fieldName === 'paymentType') {
                    header.filterObj.items = ordersFiltersDropdowns.paymentTypeList || [];
                }
                if (header.filterObj.fieldName === 'driverId') {
                    header.filterObj.items = ordersFiltersDropdowns.driverList || [];
                }
                if (header.filterObj.fieldName === 'supportDriverId') {
                    header.filterObj.items = ordersFiltersDropdowns.supportDriverList || [];
                }
                if (header.filterObj.fieldName === 'orderVehicleType') {
                    header.filterObj.items = ordersFiltersDropdowns.vehicleList || [];
                }
                if (header.filterObj.fieldName === 'status') {
                    header.filterObj.items = ordersFiltersDropdowns.orderStatusList || [];
                }
                if (header.filterObj.fieldName === 'deliveryExecutionStatus') {
                    header.filterObj.items = ordersFiltersDropdowns.executionStatusList || [];
                }
            }
            return header;
        });
        const hubsToAdd = hubCode.map((hub: string) => ({
            name: hub,
            value: hub
        }));
        let updatedFilters: any = {};
        setOrdersTableWrapperData({...ordersTableWrapperData, HeaderData: ordersHeaderData});
        setIsOrderSearched(false);
        updatedFilters = {...ordersActiveFilters, hub: {...ordersActiveFilters.hub, value: hubsToAdd}};
        if (!chipLockedByUrlRef.current) {
            setActiveOrdersFilterChip(AppConstants.EXPRESS_CONSTANTS.CONSIGNMENTS_DATA.DEFAULT_FILTER_CHIP);
        }
        if (!onLoadRef.current) {
            ordersFilterCallback(updatedFilters);
        }
        onLoadRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubCode]);

    useEffect(() => {
        if (ordersTableWrapperData && ordersTableWrapperData.HeaderData) {
            let headerData = ordersTableWrapperData.HeaderData.map((header: any) => {
                if (header.filterObj) {
                    if (header.filterObj.fieldName === 'hub') {
                        header.filterObj.items = ordersFiltersDropdowns.hubCodeList || [];
                    }
                    if (header.filterObj.fieldName === 'hubName') {
                        header.filterObj.items = ordersFiltersDropdowns.hubNameList || [];
                    }
                    if (header.filterObj.fieldName === 'deliverySlotTime') {
                        header.filterObj.items = ordersFiltersDropdowns.deliverySlotList || [];
                    }
                    if (header.filterObj.fieldName === 'deliveryStatus') {
                        header.filterObj.items = ordersFiltersDropdowns.statusList || [];
                    }
                    if (header.filterObj.fieldName === 'deliveryArea') {
                        header.filterObj.items = ordersFiltersDropdowns.deliveryArea || [];
                    }
                    if (header.filterObj.fieldName === "addressStatus") {
                        header.filterObj.items = ordersFiltersDropdowns.addressStatusList || [];
                    }
                    if (header.filterObj.fieldName === "activity") {
                        header.filterObj.items = ordersFiltersDropdowns.activityList || [];
                    }
                    if (header.filterObj.fieldName === "deliveryZone") {
                        header.filterObj.items = ordersFiltersDropdowns.deliveryZonesList || [];
                    }
                    if (header.filterObj.fieldName === 'paymentType') {
                        header.filterObj.items = ordersFiltersDropdowns.paymentTypeList || [];
                    }
                    if (header.filterObj.fieldName === 'driverId') {
                        header.filterObj.items = ordersFiltersDropdowns.driverList || [];
                    }
                    if (header.filterObj.fieldName === 'supportDriverId') {
                        header.filterObj.items = ordersFiltersDropdowns.supportDriverList || [];
                    }
                    if (header.filterObj.fieldName === 'orderVehicleType') {
                        header.filterObj.items = ordersFiltersDropdowns.vehicleList || [];
                    }
                    if (header.filterObj.fieldName === 'status') {
                        header.filterObj.items = ordersFiltersDropdowns.orderStatusList || [];
                    }
                    if (header.filterObj.fieldName === 'deliveryExecutionStatus') {
                        header.filterObj.items = ordersFiltersDropdowns.executionStatusList || [];
                    }
                }
                return header;
            });
            setOrdersTableWrapperData({...ordersTableWrapperData, HeaderData: headerData});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordersFiltersDropdowns]);

    const handleTimerInstances = (id: any) => {
        Countdown.startCountdown(id, TIMER_CONST.globalTimeForRefresh);
        setShowTime(true);
    };

    const createUpdatedFilter = (filters: FiltersDelayed) => {
      const clonedFilter = _.cloneDeep(filters);
      clonedFilter.pageNumber = 0;
      clonedFilter.pageSize = ordersTableWrapperData.loadMore.pageSize;
      return clonedFilter;
    };

    const handleToggle = () => {
        const newDelayedToggle = !delayedToggle; // Calculate the new value
        setDelayedToggle(newDelayedToggle); // Update the state
        const updatedFilters = createUpdatedFilter(ordersActiveFilters);
        setScrollToTopOnReset(true);
        // Pass the newDelayedToggle value directly to ordersFilterCallback
        ordersFilterCallback(updatedFilters, false, false, undefined, newDelayedToggle);
    };

    const fetchCourierList = useCallback(async () => {
        setCourierSwitchState(prev => ({ ...prev, loadingCouriers: true }));
        try {
            await dispatch(fetchCarrierDropdown({
                params: {
                    country: hubCountryCode,
                    deliveryType: "EXP",
                    requestType: "ORDER",
                    sourceCourier: "ewings",
                }
            }));
        } catch (error) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_FAILED_FETCH, AppConstants.SNACKBAR.TYPES.ERROR);
        } finally {
            setCourierSwitchState(prev => ({ ...prev, loadingCouriers: false }));
        }
    }, [dispatch, hubCode, hubCountryCode]);

    useEffect(() => {
        if (carrierList && carrierList.length > 0) {
            setCourierSwitchState(prev => ({ ...prev, courierList: carrierList }));
        }
    }, [carrierList]);

   

    const handleSelectionModeToggle = useCallback(async () => {
        const newMode = !courierSwitchState.isSelectionMode;
        setCourierSwitchState(prev => ({ ...prev, isSelectionMode: newMode }));
        if (newMode) {
            setCourierSwitchState(prev => ({ ...prev, deSelectCheckbox: false }));
            await fetchCourierList();
        } else {
            resetCourierSwitchState();
            triggerDeselectCheckbox();
        }
    }, [courierSwitchState.isSelectionMode, fetchCourierList, resetCourierSwitchState, triggerDeselectCheckbox]);

    const handleSelectionChange = useCallback((params: any) => {
        if (!params?.rows || !Array.isArray(params.rows)) {
            setCourierSwitchState(prev => ({ ...prev, selectedOrders: [] }));
            return;
        }
        if (params.rows.length === 0) {
            setCourierSwitchState(prev => ({ ...prev, selectedOrders: [] }));
            return;
        }
        const eligibleOrders = params.rows.filter((row: any) => 
            row.switchingAllowed && !row.inSwitchingProgress 
        );
        setCourierSwitchState(prev => ({ ...prev, selectedOrders: eligibleOrders }));
    }, [openSnackbarPopup]);

    const handleMaxSelectionReached = useCallback(() => {
        openSnackbarPopup(
          `Maximum selection limit reached. You can select up to ${ExpressConstants.EXPRESS_CONSTANTS.COURIER_SWITCHING.MAX_SWITCHING_SELECTION_COUNT} consignments at a time`,
          AppConstants.SNACKBAR.TYPES.WARNING
        );
    }, [openSnackbarPopup]);

    const handleCourierApply = useCallback(async (courierId: string) => {
        if (!courierId || courierSwitchState.selectedOrders.length === 0) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_SELECT_ORDERS, AppConstants.SNACKBAR.TYPES.ERROR);
            return;
        }
        setCourierSwitchState(prev => ({ ...prev, selectedCourier: courierId }));
        try {
            const orderIds = courierSwitchState.selectedOrders.map(order => order.consignmentId);
            await dispatch(switchCourier({
                params: {
                    fromCourier: "ewings",
                    toCourier: courierId,
                    consignmentCodes: orderIds
                }
            }));
        } catch (error) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_FAILED_SWITCH, AppConstants.SNACKBAR.TYPES.ERROR);
        }
    }, [courierSwitchState.selectedOrders, dispatch, openSnackbarPopup]);

    useEffect(() => {
        if (switchInfo  && (switchInfo.successfulShipments || switchInfo.failedShipments)) {
            const successIds = switchInfo.successfulShipments || [];
            const failedIds = (switchInfo.failedShipments || []).map((shipment: any) => shipment.shipmentId);
            setCourierSwitchState(prev => ({
                ...prev,
                switchResults: { successIds, failedIds },
                showResultDialog: true,
                isConfirmingSwitch: false
            }));
        }
    }, [switchInfo]);

    const handleCourierClear = useCallback(() => {
        setCourierSwitchState(prev => ({ ...prev, selectedCourier: '' }));
    }, []);

    const handleClearCourierSwitch = useCallback(() => {
        resetCourierSwitchState();
        triggerDeselectCheckbox();
        dispatch(resetCourierState())
    }, [resetCourierSwitchState, triggerDeselectCheckbox, dispatch]);

    const handleConfirmSwitch = useCallback(async () => {
        if (!courierSwitchState.selectedCourier || courierSwitchState.switchResults.successIds.length === 0) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_MISSING_INFO, AppConstants.SNACKBAR.TYPES.ERROR);
            return;
        }

        setCourierSwitchState(prev => ({ ...prev, isConfirmingSwitch: true }));
        try {
            await dispatch(confirmCourierSwitch({
                params: {
                    fromCourier: "ewings",
                    toCourier: courierSwitchState.selectedCourier,
                    consignmentCodes: courierSwitchState.switchResults.successIds,
                    switchedBy: switchInfo.switchedBy,
                    country: hubCountryCode
                }
            }));
        } catch (error) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_FAILED_CONFIRM, AppConstants.SNACKBAR.TYPES.ERROR);
            setCourierSwitchState(prev => ({ ...prev, isConfirmingSwitch: false }));
        }
    }, [switchInfo, hubCountryCode, courierSwitchState.selectedCourier, courierSwitchState.switchResults.successIds, dispatch, openSnackbarPopup]);

    useEffect(() => {
        if (courierSwitchSuccess && courierSwitchState.isConfirmingSwitch) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_SUCCESS, AppConstants.SNACKBAR.TYPES.SUCCESS);
            setCourierSwitchState(prev => ({ ...prev, showResultDialog: false }));
            handleClearCourierSwitch();
            ordersFilterCallback(ordersActiveFilters);
            setCourierSwitchState(prev => ({ ...prev, isConfirmingSwitch: false }));
        }
    }, [courierSwitchSuccess, courierSwitchState.isConfirmingSwitch]);

    const handleDenySwitch = useCallback(async () => {
        if (!courierSwitchState.selectedCourier || courierSwitchState.switchResults.successIds.length === 0) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_MISSING_CANCEL_INFO, AppConstants.SNACKBAR.TYPES.ERROR);
            return;
        }
        try {
            await dispatch(denyCourierSwitch({
                params: {
                    fromCourier: "ewings",
                    toCourier: courierSwitchState.selectedCourier,
                    consignmentCodes: courierSwitchState.switchResults.successIds,
                    switchedBy: switchInfo.switchedBy
                }
            }));
        } catch (error) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_FAILED_CANCEL, AppConstants.SNACKBAR.TYPES.ERROR);
        }
    }, [switchInfo, courierSwitchState.selectedCourier, courierSwitchState.switchResults.successIds, dispatch, openSnackbarPopup]);

    useEffect(() => {
        if (courierSwitchCanceled) {
            openSnackbarPopup(TextConstants.CARRIER_SWITCHING_CANCELED, AppConstants.SNACKBAR.TYPES.SUCCESS);
            setCourierSwitchState(prev => ({ ...prev, showResultDialog: false }));
            resetCourierSwitchState();
            triggerDeselectCheckbox();
            ordersFilterCallback(ordersActiveFilters);
        }
    }, [courierSwitchCanceled, ordersActiveFilters]);

    const renderCustomFilter = () => {
        const showDelayed = shouldShowDelayedFilter(activeOrdersFilterChip);
        const showCourierSwitching = shouldShowCourierSwitchingFilter(activeOrdersFilterChip);
    
        return (
          <Grid container alignItems="center" style={{ marginRight: "10px" }}>
            {showDelayed && (
              <Box className={classes.customFilter}>
                <Typography style={{ fontSize: "12px" }}>
                  {TextConstants.DELAYED}
                </Typography>
                <Switch
                  checked={delayedToggle || false}
                  onChange={handleToggle}
                  color="primary"
                />
              </Box>
            )}
            {showCourierSwitching && (
              <Box className={`${classes.customFilter} ${classes.courierActionContainer}`}>
                <CourierDropdown
                  isSelectionMode={courierSwitchState.isSelectionMode}
                  selectedCourier={courierSwitchState.selectedCourier}
                  courierList={courierSwitchState.courierList}
                  loading={courierSwitchState.loadingCouriers}
                  onEnableSelection={handleSelectionModeToggle}
                  onApply={handleCourierApply}
                  onClear={handleCourierClear}
                />
              </Box>
            )}
          </Grid>
        );
      };

    // Update HeaderData when isSelectionMode changes
    useEffect(() => {
        const updatedHeaderData = ordersTableWrapperData.HeaderData.map((header: any) => {
            let newHeader = {...header};
            if (newHeader.field === 'actions') {
                newHeader.renderCell = (params: CellParams) => (
                    <Typography 
                      className={`actionsLink ${courierSwitchState.isSelectionMode ? 'disabled' : ''}`}
                    >
                      {params.value}
                    </Typography>
                )
            }
            return newHeader;
        });
        
        setOrdersTableWrapperData(prev => ({
            ...prev,
            HeaderData: updatedHeaderData
        }));
    }, [courierSwitchState.isSelectionMode]);

    return (
      <div className={classes.root}>
        {loading && <Loader></Loader>}
        {clubbingLoading && <FullScreenLoader  />}
        <Grid className={classes.container} container>
          <Grid className="planningItem content" item>
            <Grid container className={classes.tabPanelContainer}>
              <Grid item className="panelItem chipFilter">
                <Grid container className="chipsContainer">
                  {getFilterChips()}
                </Grid>
              </Grid>
              <Grid item className="panelItem gridWrapper">
                <GridWrapper
                  showHeader={true}
                  checkboxSelection={courierSwitchState.isSelectionMode}
                  headerData={ordersTableWrapperData.HeaderData}
                  rowData={ordersListToShow}
                  isTabChange={isTabChange}
                  loading={loading ? false : tableLoading}
                  searchTypes={ordersTableWrapperData.SEARCH_TYPES}
                  defaultFilter={ordersTableWrapperData.defaultFiltersObj}
                  activeFilter={ordersActiveFilters}
                  headerButtons={ordersTableWrapperData.headerButtons}
                  title={ordersTableWrapperData.title}
                  disableSelectionOnClick={!courierSwitchState.isSelectionMode}
                  sortModel={ordersTableWrapperData.sortModel as SortModel}
                  searchValue={ordersSearchValue}
                  loadMore={{
                    ...ordersTableWrapperData.loadMore,
                    rowCount: ordersData.totalElements || 0,
                  }}
                  isRowSelectable={(row: any) => row.switchingAllowed === true}
                  isRowDisabled={(row: any) => ({
                    disabled: row.inSwitchingProgress === true,
                    reason: row.inSwitchingProgress ? TextConstants.CARRIER_SWITCHING_PROGRESS: undefined
                  })}
                  maxSelectable={ExpressConstants.EXPRESS_CONSTANTS.COURIER_SWITCHING.MAX_SWITCHING_SELECTION_COUNT}
                  onMaxSelectableReached={handleMaxSelectionReached}
                  getRowClassName={(params: any) => params.row?.inSwitchingProgress ? classes.rowSwitchingProgress : ''}
                  headerBtnClick={handleOrdersHeaderButtonClick}
                  searchCallback={ordersSearchFilterHandler}
                  filterGrid={ordersFilterCallback}
                  onCellClick={handleConsignmentCellClick}
                  showCustomFilters={shouldShowCustomFilter(activeOrdersFilterChip)}
                  renderCustomFilter={shouldShowCustomFilter(activeOrdersFilterChip) ? renderCustomFilter : undefined}
                  scrollToTopOnReset={scrollToTopOnReset}
                  deSelectCheckbox={courierSwitchState.deSelectCheckbox}
                  onSelectionChange={handleSelectionChange}
                  showRefreshButton={true}
                />
              </Grid>
            </Grid>
          </Grid>
          {!error && renderExcelProgress
            ? excelLoading && (
                <Grid container className={classes.excelDownloadStatus}>
                  <Grid item>
                    <Typography className={classes.excelDownloadStatusText}>
                      {AppConstants.EXPORT_FILE_CONSTANT.DOWNLOAD_INITIATED}
                    </Typography>
                  </Grid>
                  <Loader />
                </Grid>
              )
            : exportError === AppConstants.ERRORS.INTERNAL_SERVER_ERROR &&
              renderExcelProgress && (
                <Grid container className={classes.excelDownloadError}>
                  <Grid item>
                    <Typography className={classes.excelDownloadStatusText}>
                      {AppConstants.EXPORT_FILE_CONSTANT.DOWNLOAD_FAIL}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.container}>
                    <IconButton
                      aria-label="close"
                      className={classes.close}
                      onClick={handleProgressClose}
                    >
                      <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                  </Grid>
                </Grid>
              )}
          {openConsignmentDetailsPopup && (
            <ExpressOrderDetails
              open={openConsignmentDetailsPopup}
              consignment={consignmentData}
              closePopup={closeConsignmentDetailsPopup}
              handleTimerInstances={handleTimerInstances}
              timerStarted={showTime}
              setShowTime={setShowTime}
              handleDownloadLogs={handleDownloadLogs}
              getRapydActivityImage={getRapydActivityImage}
            />
          )}
          {openDriverAssignmentPopup && (
            <DriverAssignment
            open={openDriverAssignmentPopup}
            consignment={consignmentData}
            closePopup={closeDriverAssignmentPopup}
          />)}
          <CustomSnackbar
            open={openSnackbar}
            handleClose={handleSnackbarClose}
            onExited={handleSnackbarExited}
            autoHideDuration={AppConstants.SNACKBAR.AUTO_HIDE_TIMEOUT}
            message={snackbarMessage.current}
            type={snackbarType.current}
          />
          <ExportInitiatedDialogProvider />
          {courierSwitchState.showResultDialog && <CourierSwitchResultDialog
            open={courierSwitchState.showResultDialog}
            successIds={courierSwitchState.switchResults.successIds}
            failedIds={courierSwitchState.switchResults.failedIds}
            selectedCourier={courierSwitchState.selectedCourier}
            onConfirm={handleConfirmSwitch}
            onDiscard={handleDenySwitch}
            onDiscardComplete={handleClearCourierSwitch}
          />}
        </Grid>
      </div>
    );
}

export default Express;